import { useState } from "react";
import { Button, List, Modal, Tooltip, Typography } from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useVisitHistory, type VisitEntry } from "../context/HistoryContext";
import { locateNav } from "../helpers/nav";

/** Where the page sits in the nav, used to tell two similarly named pages apart. */
function contextOf(entry: VisitEntry): string | undefined {
  const { module, item } = locateNav(entry.key);
  const trail = [module?.label, item?.label].filter(Boolean).join(" › ");
  return trail && trail !== entry.label ? trail : undefined;
}

/**
 * The last five pages visited, sitting opposite the breadcrumbs: the trail says how you got
 * here, this says where you have been. Inline links on a desktop, a modal behind a button on
 * a phone, where the crumb row has no width to spare.
 */
export function RecentPages() {
  const { entries } = useVisitHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const recent = entries.filter((entry) => entry.key !== location.pathname).slice(0, 5);
  if (recent.length === 0) return null;

  return (
    <>
      <nav className="glut-recent" aria-label="Recently visited">
        <span className="glut-recent-label">
          <HistoryOutlined /> Recent
        </span>
        {recent.map((entry) => {
          const context = contextOf(entry);
          return (
            <Tooltip key={entry.key} title={context}>
              <Link to={entry.path} className="glut-recent-link">
                {entry.label}
              </Link>
            </Tooltip>
          );
        })}
      </nav>

      <Button className="glut-recent-button" size="small" icon={<HistoryOutlined />} onClick={() => setOpen(true)}>
        Recent
      </Button>
      <Modal open={open} onCancel={() => setOpen(false)} footer={null} title="Recently visited">
        <List
          dataSource={recent}
          renderItem={(entry) => {
            const context = contextOf(entry);
            return (
              <List.Item>
                <Link to={entry.path} onClick={() => setOpen(false)}>
                  {entry.label}
                  {context && (
                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                      {context}
                    </Typography.Text>
                  )}
                </Link>
              </List.Item>
            );
          }}
        />
      </Modal>
    </>
  );
}
